import React, { useCallback, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Image } from "react-bootstrap";
import Loader from "../../components/Loader";
import MetaData from "../../components/layout/MetaData";
import { useGetCompareProductsQuery } from "../../redux/api/productsApi";
import {
  formatCompareValue,
  getManufacturerPartNumber,
  getProductDescription,
  getProductImageUrl,
  getVisibleAttributeKeys,
  isSharedCompareValue,
  clearStoredCompareProducts,
  removeStoredCompareProduct,
} from "../../utils/productCompare";
import { formatThousandsNumber } from "../../utils/numberFormat";

/**
 * 从 URL 查询参数中解析商品 ID 列表。
 * @param {URLSearchParams} searchParams URL 查询参数
 * @returns {string[]} 商品 ID 列表
 */
const getCompareIdsFromSearch = (searchParams) => {
  return (searchParams.get("ids") || "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
};

/**
 * 格式化商品价格，保持对比页价格展示统一。
 * @param {number|string} price 商品价格
 * @returns {string} 价格文本
 */
const formatProductPrice = (price) => {
  if (price === undefined || price === null || price === "") {
    return "$ 0";
  }

  return `$ ${price}`;
};

/**
 * 更新对比页 URL 中的商品 ID 列表。
 * @param {(path: string) => void} navigate 路由跳转函数
 * @param {string[]} ids 商品 ID 列表
 */
const updateCompareUrl = (navigate, ids) => {
  if (ids.length) {
    navigate(`/products/compare?ids=${ids.join(",")}`);
    return;
  }

  navigate("/products");
};

/**
 * 构建对比页基础属性行。
 * @param {(id: string) => void} onRemove 删除商品回调
 * @returns {Array<Record<string, any>>} 基础属性行
 */
const getBaseCompareRows = (onRemove) => {
  return [
    {
      key: "image",
      label: "Image",
      alwaysShow: true,
      render: (product) => (
        <div className="relative mx-auto flex h-[82px] w-[100px] items-center justify-center">
          <Image
            src={getProductImageUrl(product)}
            alt={product?.name}
            className="max-h-[72px] max-w-[88px] object-contain"
          />
          <button
            type="button"
            onClick={() => onRemove(product?._id)}
            className="absolute right-0 top-0 flex h-5 w-5 items-center justify-center rounded-full text-xl leading-none text-red-600 hover:bg-red-50"
            aria-label={`Remove ${product?.name || "product"}`}
          >
            x
          </button>
        </div>
      ),
      getValue: (product) => getProductImageUrl(product),
    },
    {
      key: "mfrPart",
      label: "Mfr Part #",
      alwaysShow: true,
      render: (product) => (
        <Link to={`/products/${product?._id}`} className="font-bold text-blue-700 hover:text-orange-500">
          {getManufacturerPartNumber(product)}
        </Link>
      ),
      getValue: (product) => getManufacturerPartNumber(product),
    },
    {
      key: "mfr",
      label: "Mfr",
      alwaysShow: true,
      render: (product) => formatCompareValue(product?.Manufacturer),
      getValue: (product) => product?.Manufacturer,
    },
    {
      key: "supplier",
      label: "Supplier",
      alwaysShow: true,
      render: (product) => formatCompareValue(product?.Supplier || product?.Manufacturer),
      getValue: (product) => product?.Supplier || product?.Manufacturer,
    },
    {
      key: "ctyeaPart",
      label: "CTYEA Part #",
      alwaysShow: true,
      render: (product) => formatCompareValue(product?.CTYEA_Part_Number),
      getValue: (product) => product?.CTYEA_Part_Number,
    },
    {
      key: "description",
      label: "Description",
      alwaysShow: true,
      render: (product) => getProductDescription(product),
      getValue: (product) => getProductDescription(product),
    },
    {
      key: "price",
      label: "Price",
      alwaysShow: true,
      render: (product) => formatProductPrice(product?.price),
      getValue: (product) => product?.price,
    },
    {
      key: "stock",
      label: "Stock",
      alwaysShow: true,
      render: (product) => formatThousandsNumber(product?.stock),
      getValue: (product) => product?.stock,
    },
    {
      key: "minQty",
      label: "Min Qty",
      alwaysShow: true,
      render: (product) => formatCompareValue(product?.Minimum_Order_Quantity || product?.minQty || 1),
      getValue: (product) => product?.Minimum_Order_Quantity || product?.minQty || 1,
    },
  ];
};

/**
 * 构建动态属性对比行。
 * @param {string[]} attributeKeys 动态属性键
 * @returns {Array<Record<string, any>>} 动态属性行
 */
const getDynamicCompareRows = (attributeKeys) => {
  return attributeKeys.map((key) => ({
    key: `attribute-${key}`,
    label: key,
    alwaysShow: false,
    render: (product) => formatCompareValue(product?.product_Attributes?.[key]),
    getValue: (product) => product?.product_Attributes?.[key],
  }));
};

/**
 * 渲染商品零件对比页。
 * @returns {React.JSX.Element} 商品零件对比页
 */
const ProductComparePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [hideSharedAttributes, setHideSharedAttributes] = useState(false);
  const compareIds = useMemo(() => getCompareIdsFromSearch(searchParams), [searchParams]);
  const { data, isLoading, isError } = useGetCompareProductsQuery(compareIds, {
    skip: compareIds.length < 1,
  });
  const products = useMemo(() => data?.products || [], [data?.products]);
  const attributeKeys = useMemo(() => getVisibleAttributeKeys(products), [products]);

  /**
   * 从对比页移除指定商品，并同步更新 URL 参数。
   * @param {string} productId 商品 ID
   */
  const handleRemoveProduct = useCallback((productId) => {
    removeStoredCompareProduct(productId);
    const nextIds = compareIds.filter((id) => id !== productId);
    updateCompareUrl(navigate, nextIds);
  }, [compareIds, navigate]);

  /**
   * 关闭商品对比功能，清空已选商品并返回商品列表页。
   */
  const handleCloseCompare = () => {
    clearStoredCompareProducts();
    navigate("/products");
  };

  const rows = useMemo(() => {
    return [
      ...getBaseCompareRows(handleRemoveProduct),
      ...getDynamicCompareRows(attributeKeys),
    ];
  }, [attributeKeys, handleRemoveProduct]);

  const sharedAttributeCount = rows.filter(
    (row) => !row.alwaysShow && isSharedCompareValue(products, row.getValue)
  ).length;
  const visibleRows = hideSharedAttributes
    ? rows.filter((row) => row.alwaysShow || !isSharedCompareValue(products, row.getValue))
    : rows;

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <MetaData title="Compare Products" />
      <main className="bg-[#F5F9FF] px-3 py-6 sm:px-8 lg:px-24">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Compare Products</h1>
            <p className="mt-1 text-sm text-gray-500">{products.length} products selected</p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={handleCloseCompare}
              className="min-h-[42px] rounded border border-gray-300 bg-white px-4 py-2 text-sm font-bold text-[#00144f] hover:border-red-500 hover:text-red-600"
            >
              Close Compare
            </button>
            <button
              type="button"
              onClick={() => setHideSharedAttributes((current) => !current)}
              className="min-h-[42px] rounded border border-gray-300 bg-white px-4 py-2 text-sm font-bold text-[#00144f] hover:border-[#2684fe] hover:text-[#2684fe]"
            >
              {hideSharedAttributes
                ? "Show Shared Attributes"
                : `Hide Shared Attributes (${sharedAttributeCount})`}
            </button>
            <Link
              to="/products"
              className="inline-flex min-h-[42px] items-center justify-center rounded bg-[#FF230D] px-5 py-2 text-sm font-bold text-white no-underline hover:bg-[#d91d0b]"
            >
              Add products
            </Link>
          </div>
        </div>

        {isError && (
          <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            Failed to load compare products.
          </div>
        )}

        {products.length < 2 ? (
          <div className="rounded border border-gray-200 bg-white px-5 py-8 text-center">
            <p className="mb-4 text-sm font-medium text-gray-600">
              Please select at least 2 products to compare.
            </p>
            <Link
              to="/products"
              className="inline-flex min-h-[38px] items-center rounded bg-[#2684fe] px-4 py-2 text-sm font-bold text-white no-underline hover:bg-blue-600"
            >
              Back to Products
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto border border-gray-200 bg-white">
            <table
              className="w-full border-collapse text-sm"
              style={{ minWidth: `${160 + products.length * 190}px` }}
            >
              <tbody>
                {visibleRows.map((row, rowIndex) => (
                  <tr key={row.key} className={rowIndex % 2 ? "bg-white" : "bg-[#F7F8FB]"}>
                    <th className="sticky left-0 z-10 w-[160px] border-r border-gray-200 bg-inherit px-3 py-4 text-left text-xs font-bold text-gray-900">
                      {row.label}
                    </th>
                    {products.map((product) => (
                      <td
                        key={`${row.key}-${product?._id}`}
                        className="min-w-[190px] border-r border-gray-200 px-3 py-4 text-center text-xs font-medium text-gray-900"
                        title={formatCompareValue(row.getValue(product))}
                      >
                        {row.render(product)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </>
  );
};

export default ProductComparePage;
